import * as React from 'react';
import { createContext, useContext, useState, useCallback } from 'react';
import { ToastVariant, ToastVariantBase, normalizeVariant } from '../lib/toast';

interface Toast {
  id: string;
  message: string;
  variant: ToastVariantBase;
}

interface ToastContextType {
  enqueue: (message: string, variant?: ToastVariant) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextType>({ enqueue: () => {}, dismiss: () => {} });
export const useToast = () => useContext(ToastContext);

type ToastProviderProps = React.PropsWithChildren<{}>;

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const enqueue = useCallback((message: string, variant?: ToastVariant) => {
    const v = normalizeVariant(variant);
    const id = Math.random().toString(36).slice(2);
    setToasts(ts => [...ts, { id, message, variant: v }]);
    setTimeout(() => setToasts(ts => ts.filter(t => t.id !== id)), 4000);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts(ts => ts.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ enqueue, dismiss }}>
      {children}
      <div aria-live="polite" aria-atomic="true" style={{ position: 'fixed', top: 24, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {toasts.map(t => (
          <div
            key={t.id}
            role={t.variant === 'danger' ? 'alert' : 'status'}
            style={{
              minWidth: 220,
              maxWidth: 320,
              background:
                t.variant === 'success' ? '#2e7d32'
                : t.variant === 'danger' ? '#e33'
                : t.variant === 'warning' ? '#f9a825'
                : t.variant === 'info' ? '#333'
                : '#333',
              color: '#fff',
              borderRadius: 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              padding: '12px 24px',
              fontSize: 15,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'opacity 150ms',
              opacity: 1,
            }}
            tabIndex={0}
            onClick={() => dismiss(t.id)}
            onKeyDown={(event) => (event.key === 'Enter' || event.key === ' ') && dismiss(t.id)}
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
